import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Opportunity, QualifyChecklist, StaffMember, QualifySuggestion } from "../../types";
import {
  CheckCircle2,
  XCircle,
  HelpCircle,
  Sparkles,
  Compass,
  ShieldCheck,
  Award,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { useStaffDirectory } from "../../services/staffService";

interface QualifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity | null;
  staff?: StaffMember[];
  onSave: (checklist: QualifyChecklist) => Promise<void>;
}

type TabKey = "strategic" | "compliance" | "decision";

const QualifyForm: React.FC<Omit<QualifyModalProps, "isOpen">> = ({
  onClose,
  opportunity,
  staff = [],
  onSave,
}) => {
  const q = opportunity?.qualifyChecklist;
  const [activeTab, setActiveTab] = useState<TabKey>("strategic");

  // Tab 1: Strategic Fit States
  const [expScore, setExpScore] = useState<"strong" | "moderate" | "weak" | "none">(
    q?.experienceInArea?.score || "moderate",
  );
  const [expNotes, setExpNotes] = useState(q?.experienceInArea?.notes || "");

  const [volumeEstimate, setVolumeEstimate] = useState(q?.potentialVolume?.estimate || "");
  const [volumeNotes, setVolumeNotes] = useState(q?.potentialVolume?.notes || "");

  const [effortLevel, setEffortLevel] = useState<"low" | "medium" | "high">(
    q?.effortToRespond?.level || "medium",
  );
  const [effortNotes, setEffortNotes] = useState(q?.effortToRespond?.notes || "");

  // Tab 2: Compliance & Feasibility States
  const [securityCompatible, setSecurityCompatible] = useState(
    q?.securityHipaaRequirements?.compatible ?? true,
  );
  const [securityNotes, setSecurityNotes] = useState(q?.securityHipaaRequirements?.notes || "");

  const [wbeApplies, setWbeApplies] = useState(q?.wbePreferenceOrGoals?.applies ?? true);
  const [wbePercentage, setWbePercentage] = useState(q?.wbePreferenceOrGoals?.percentage || "");
  const [wbeNotes, setWbeNotes] = useState(q?.wbePreferenceOrGoals?.notes || "");

  const [mandatoryMeets, setMandatoryMeets] = useState(
    q?.mandatoryQualifications?.meetsAll ?? true,
  );
  const [mandatoryUnmet, setMandatoryUnmet] = useState(
    q?.mandatoryQualifications?.unmetDetails || "",
  );

  const [deadlineFeasible, setDeadlineFeasible] = useState(
    q?.deadlineFeasibility?.feasible ?? true,
  );
  const [deadlineNotes, setDeadlineNotes] = useState(q?.deadlineFeasibility?.notes || "");

  // Tab 3: Final Decision States
  const [decision, setDecision] = useState<"go" | "no_go" | "undecided">(
    q?.decision || "undecided",
  );
  const [deciderName, setDeciderName] = useState(
    q?.deciderName || staff[0]?.displayName || "Katie",
  );
  const [decisionReason, setDecisionReason] = useState(q?.decisionReason || "");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!opportunity) return;

    if (decision === "no_go" && !decisionReason.trim()) {
      setActiveTab("decision");
      setError("Please provide a reason explaining the No-Go decision.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const selectedStaff = staff.find((s) => s.displayName === deciderName);

    const checklist: QualifyChecklist = {
      opportunityId: opportunity.id,
      experienceInArea: { score: expScore, notes: expNotes.trim() },
      potentialVolume: { estimate: volumeEstimate.trim(), notes: volumeNotes.trim() },
      effortToRespond: { level: effortLevel, notes: effortNotes.trim() },
      securityHipaaRequirements: { compatible: securityCompatible, notes: securityNotes.trim() },
      wbePreferenceOrGoals: {
        applies: wbeApplies,
        ...(wbePercentage.trim() ? { percentage: wbePercentage.trim() } : {}),
        notes: wbeNotes.trim(),
      },
      mandatoryQualifications: {
        meetsAll: mandatoryMeets,
        ...(mandatoryUnmet.trim() ? { unmetDetails: mandatoryUnmet.trim() } : {}),
      },
      deadlineFeasibility: { feasible: deadlineFeasible, notes: deadlineNotes.trim() },
      decision,
      deciderName: deciderName || "Unassigned",
      deciderEmail: selectedStaff?.email || "",
      decisionDate: new Date().toISOString().slice(0, 10),
      decisionReason: decisionReason.trim(),
      updatedAt: new Date().toISOString(),
    };

    try {
      await onSave(checklist);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record qualification.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderSuggestion = (criterionId: string, onApply: (sug: QualifySuggestion) => void) => {
    const sug = opportunity?.qualifySuggestions?.find((s) => s.criterionId === criterionId);
    if (!sug) return null;
    return (
      <div className="mt-2 flex items-center justify-between rounded-lg border border-teal-200/80 bg-teal-50/70 px-2.5 py-1.5 text-xs text-teal-900 dark:border-teal-900/50 dark:bg-teal-950/30 dark:text-teal-200">
        <div className="flex items-start gap-1.5 overflow-hidden pr-2">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 text-[#2ba5b5] shrink-0" />
          <span className="truncate">
            <strong className="font-semibold">{sug.suggestion}</strong>: {sug.evidence}
            {sug.sourcePage ? ` (p. ${sug.sourcePage})` : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onApply(sug)}
          className="btn-animate shrink-0 rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-[#2ba5b5] border border-teal-200 hover:bg-teal-100 dark:bg-slate-900 dark:border-teal-800"
        >
          Use Suggestion
        </button>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab("strategic")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-bold transition-colors ${
            activeTab === "strategic"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <Compass className="h-4 w-4" />
          <span>1. Strategic Fit (3)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("compliance")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-bold transition-colors ${
            activeTab === "compliance"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <ShieldCheck className="h-4 w-4" />
          <span>2. Compliance & Feasibility (4)</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("decision")}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-bold transition-colors ${
            activeTab === "decision"
              ? "border-[#2ba5b5] text-[#2ba5b5]"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          <Award className="h-4 w-4" />
          <span>3. Bid Decision & Sign-off</span>
          {decision !== "undecided" && (
            <span
              className={`rounded-full px-1.5 py-0.2 text-[10px] uppercase font-extrabold ${
                decision === "go"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300"
              }`}
            >
              {decision.replace("_", "-")}
            </span>
          )}
        </button>
      </div>

      {/* Tab 1: Strategic Fit */}
      {activeTab === "strategic" && (
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 text-sm">
          {/* 1. Experience in the Area */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  1. Experience in Area / Past Performance
                </label>
                <p className="text-[11px] text-slate-500">
                  Relevance of past contracts, translation domain expertise, and client references.
                </p>
              </div>
              <div className="flex gap-1.5">
                {(["strong", "moderate", "weak", "none"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setExpScore(s)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase transition-colors ${
                      expScore === s
                        ? "bg-[#2ba5b5] text-white"
                        : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={expNotes}
              onChange={(e) => setExpNotes(e.target.value)}
              placeholder="Notes on past performance, similar contracts, or references..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("experienceInArea", (sug) => {
              setExpNotes(sug.evidence);
              const lower = sug.suggestion.toLowerCase();
              if (["strong", "moderate", "weak", "none"].includes(lower)) {
                setExpScore(lower as "strong" | "moderate" | "weak" | "none");
              }
            })}
          </div>

          {/* 2. Potential Volume */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  2. Potential Volume / Contract Value
                </label>
                <p className="text-[11px] text-slate-500">
                  Expected annual spend, minimum guarantees, or total procurement value.
                </p>
              </div>
              <input
                type="text"
                value={volumeEstimate}
                onChange={(e) => setVolumeEstimate(e.target.value)}
                placeholder="e.g., $150k - $300k / year"
                className="w-48 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 text-right font-medium"
              />
            </div>
            <input
              type="text"
              value={volumeNotes}
              onChange={(e) => setVolumeNotes(e.target.value)}
              placeholder="Volume guarantees, historical spend, or minimum budget..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("potentialVolume", (sug) => {
              setVolumeEstimate(sug.suggestion);
              setVolumeNotes(sug.evidence);
            })}
          </div>

          {/* 3. Effort to Respond */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  3. Effort to Respond
                </label>
                <p className="text-[11px] text-slate-500">
                  Resource investment needed: sample translations, custom security forms, portal
                  questionnaires.
                </p>
              </div>
              <div className="flex gap-1.5">
                {(["low", "medium", "high"] as const).map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    onClick={() => setEffortLevel(lvl)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase transition-colors ${
                      effortLevel === lvl
                        ? "bg-[#2ba5b5] text-white"
                        : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                    }`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text"
              value={effortNotes}
              onChange={(e) => setEffortNotes(e.target.value)}
              placeholder="Page limit, complex sample translations, portal forms, cost templates..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("effortToRespond", (sug) => {
              setEffortNotes(sug.evidence);
              const lower = sug.suggestion.toLowerCase();
              if (["low", "medium", "high"].includes(lower)) {
                setEffortLevel(lower as "low" | "medium" | "high");
              }
            })}
          </div>
        </div>
      )}

      {/* Tab 2: Compliance & Feasibility */}
      {activeTab === "compliance" && (
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 text-sm">
          {/* 4. Security & HIPAA Requirements */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  4. Security & HIPAA Safeguards
                </label>
                <p className="text-[11px] text-slate-500">
                  SOC 2, ISO 27001, BAA requirement, or custom encryption ciphers.
                </p>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setSecurityCompatible(true)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    securityCompatible
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Compatible
                </button>
                <button
                  type="button"
                  onClick={() => setSecurityCompatible(false)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    !securityCompatible
                      ? "bg-rose-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Issue / Incompatible
                </button>
              </div>
            </div>
            <input
              type="text"
              value={securityNotes}
              onChange={(e) => setSecurityNotes(e.target.value)}
              placeholder="e.g., Standard BAA compatible, SOC 2 Type II report available upon request..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("securityHipaaRequirements", (sug) => {
              setSecurityNotes(sug.evidence);
              const lower = sug.suggestion.toLowerCase();
              setSecurityCompatible(!lower.includes("incompatible") && !lower.includes("conflict"));
            })}
          </div>

          {/* 5. WBE Preference or Goals */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  5. WBE Preference or Subcontracting Goals
                </label>
                <p className="text-[11px] text-slate-500">
                  Multilingual Connections is WBE-certified. Check if evaluation credits apply.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={wbePercentage}
                  onChange={(e) => setWbePercentage(e.target.value)}
                  placeholder="e.g., 20% Goal"
                  className="w-28 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 text-right"
                />
                <button
                  type="button"
                  onClick={() => setWbeApplies(!wbeApplies)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    wbeApplies
                      ? "bg-[#2ba5b5] text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  {wbeApplies ? "Applies" : "N/A"}
                </button>
              </div>
            </div>
            <input
              type="text"
              value={wbeNotes}
              onChange={(e) => setWbeNotes(e.target.value)}
              placeholder="WBE point evaluation bonus or direct prime credit..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("wbePreferenceOrGoals", (sug) => {
              setWbeNotes(sug.evidence);
              setWbeApplies(!sug.suggestion.toLowerCase().includes("none"));
            })}
          </div>

          {/* 6. Mandatory Minimum Qualifications */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  6. Mandatory Minimum Qualifications
                </label>
                <p className="text-[11px] text-slate-500">
                  Hard eligibility requirements: years in business, insurance limits, ISO
                  certifications.
                </p>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setMandatoryMeets(true)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    mandatoryMeets
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Meets All
                </button>
                <button
                  type="button"
                  onClick={() => setMandatoryMeets(false)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    !mandatoryMeets
                      ? "bg-rose-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Gaps Found
                </button>
              </div>
            </div>
            {!mandatoryMeets && (
              <input
                type="text"
                value={mandatoryUnmet}
                onChange={(e) => setMandatoryUnmet(e.target.value)}
                placeholder="Detail the mandatory requirement(s) that MLC does not currently satisfy..."
                className="mt-3 w-full rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-rose-500 dark:border-rose-800 dark:bg-slate-950 dark:text-slate-100"
              />
            )}
            {renderSuggestion("mandatoryQualifications", (sug) => {
              const lower = sug.suggestion.toLowerCase();
              const meets =
                !lower.includes("unmet") && !lower.includes("gap") && !lower.includes("no");
              setMandatoryMeets(meets);
              if (!meets) setMandatoryUnmet(sug.evidence);
            })}
          </div>

          {/* 7. Deadline Feasibility */}
          <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold uppercase text-slate-700 dark:text-slate-300">
                  7. Submission Deadline Feasibility
                </label>
                <p className="text-[11px] text-slate-500">
                  Timeline runway for questions, writing, red team review, and final packaging.
                </p>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setDeadlineFeasible(true)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    deadlineFeasible
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Feasible
                </button>
                <button
                  type="button"
                  onClick={() => setDeadlineFeasible(false)}
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                    !deadlineFeasible
                      ? "bg-rose-600 text-white"
                      : "bg-white text-slate-600 border border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                  }`}
                >
                  Tight / Risky
                </button>
              </div>
            </div>
            <input
              type="text"
              value={deadlineNotes}
              onChange={(e) => setDeadlineNotes(e.target.value)}
              placeholder="e.g., 2 weeks available; questions deadline is Nov 4..."
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
            {renderSuggestion("deadlineFeasibility", (sug) => {
              setDeadlineNotes(sug.evidence);
              const lower = sug.suggestion.toLowerCase();
              setDeadlineFeasible(
                !lower.includes("tight") && !lower.includes("unfeasible") && !lower.includes("no"),
              );
            })}
          </div>
        </div>
      )}

      {/* Tab 3: Final Decision & Sign-off */}
      {activeTab === "decision" && (
        <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1 text-sm">
          {/* Quick Scorecard Review */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl bg-slate-50 p-3 text-xs dark:bg-slate-900/60 border border-slate-200/60 dark:border-slate-800">
            <div>
              <span className="text-[10px] uppercase font-semibold text-slate-500 block">
                Area Exp.
              </span>
              <span className="font-bold uppercase text-slate-800 dark:text-slate-200">
                {expScore}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-semibold text-slate-500 block">
                Effort
              </span>
              <span className="font-bold uppercase text-slate-800 dark:text-slate-200">
                {effortLevel}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-semibold text-slate-500 block">
                Security
              </span>
              <span
                className={`font-bold ${securityCompatible ? "text-emerald-600" : "text-rose-600"}`}
              >
                {securityCompatible ? "Compatible" : "Issue"}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase font-semibold text-slate-500 block">
                Mandatory
              </span>
              <span
                className={`font-bold ${mandatoryMeets ? "text-emerald-600" : "text-rose-600"}`}
              >
                {mandatoryMeets ? "Meets All" : "Gaps"}
              </span>
            </div>
          </div>

          {/* DECISION SECTION */}
          <div className="rounded-xl border-2 border-[#2ba5b5]/30 bg-teal-50/30 p-4 dark:border-teal-800/40 dark:bg-teal-950/20 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                  Qualification Decision
                </h4>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Selecting Bid (Go) or Decline (No-Go) automatically synchronizes the pipeline
                  stage.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDecision("go")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold transition-all ${
                    decision === "go"
                      ? "bg-emerald-600 text-white shadow-xs"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>BID (GO)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setDecision("no_go")}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold transition-all ${
                    decision === "no_go"
                      ? "bg-rose-600 text-white shadow-xs"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  }`}
                >
                  <XCircle className="h-4 w-4" />
                  <span>DECLINE (NO-GO)</span>
                </button>

                <button
                  type="button"
                  onClick={() => setDecision("undecided")}
                  className={`inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
                    decision === "undecided"
                      ? "bg-slate-600 text-white"
                      : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                  }`}
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>Undecided</span>
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
                  Decider (Staff HQ)
                </label>
                <select
                  value={deciderName}
                  onChange={(e) => setDeciderName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  {staff.map((s) => (
                    <option key={s.id} value={s.displayName}>
                      {s.displayName} {s.department ? `(${s.department})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="qualify-reason"
                  className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
                >
                  Decision Justification / Reason{" "}
                  {decision === "no_go" && <span className="text-rose-500">*</span>}
                </label>
                <input
                  id="qualify-reason"
                  type="text"
                  value={decisionReason}
                  onChange={(e) => setDecisionReason(e.target.value)}
                  placeholder={
                    decision === "no_go"
                      ? "Explain why declining (e.g., insufficient runway, low margin)..."
                      : "Rationale for pursuing bid..."
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer Controls */}
      <div className="flex items-center justify-between border-t border-slate-200/80 pt-3 dark:border-slate-800">
        <div className="flex gap-2">
          {activeTab === "compliance" && (
            <button
              type="button"
              onClick={() => setActiveTab("strategic")}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Back: Strategic Fit</span>
            </button>
          )}
          {activeTab === "decision" && (
            <button
              type="button"
              onClick={() => setActiveTab("compliance")}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Back: Compliance</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {activeTab === "strategic" && (
            <button
              type="button"
              onClick={() => setActiveTab("compliance")}
              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200"
            >
              <span>Next: Compliance & Feasibility</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}

          {activeTab === "compliance" && (
            <button
              type="button"
              onClick={() => setActiveTab("decision")}
              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#238997]"
            >
              <span>Next: Bid Decision</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="btn-animate rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#238997] disabled:opacity-50"
          >
            {submitting ? "Saving..." : "Save Qualification Decision"}
          </button>
        </div>
      </div>
    </form>
  );
};

export const QualifyModal: React.FC<QualifyModalProps> = (props) => {
  const { staff: directoryStaff } = useStaffDirectory();
  const effectiveStaff = props.staff && props.staff.length > 0 ? props.staff : directoryStaff;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      maxWidth="4xl"
      title={`Qualify Checklist — ${props.opportunity?.client || "RFP"}`}
      description="Procurement qualification assessment across strategic fit, compliance, and decision criteria."
    >
      <QualifyForm
        key={props.opportunity?.id || "new-qualify"}
        onClose={props.onClose}
        opportunity={props.opportunity}
        staff={effectiveStaff}
        onSave={props.onSave}
      />
    </Modal>
  );
};
